import { SESSION_IDLE_MS, utcDayKey } from '@xshop/shared';

// sessionStorage, never a cookie and never localStorage: the key dies with the tab and cannot identify a returning visitor.
const STORAGE_KEY = 'xshop_sf_session';

// One visit as the tab remembers it.
export interface StorefrontSession {
  key: string;
  startedAt: number;
  lastSeenAt: number;
}

// Used only when sessionStorage is unavailable (private mode, embedded webviews).
let memorySession: StorefrontSession | null = null;

// Mints an opaque 32-character key with the platform CSPRNG, falling back to Math.random.
function newKey(): string {
  const crypto = typeof window !== 'undefined' ? window.crypto : undefined;
  if (crypto?.getRandomValues) {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`.padEnd(24, '0').slice(0, 32);
}

// Reads the tab's stored session, returning null when nothing usable is there.
function readStored(): StorefrontSession | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return memorySession;
    const parsed = JSON.parse(raw) as Partial<StorefrontSession>;
    if (typeof parsed.key !== 'string' || typeof parsed.startedAt !== 'number') return null;
    return { key: parsed.key, startedAt: parsed.startedAt, lastSeenAt: parsed.lastSeenAt ?? parsed.startedAt };
  } catch {
    return memorySession;
  }
}

// Persists the session for the tab; a storage failure downgrades to memory, never to an error.
function writeStored(session: StorefrontSession): void {
  memorySession = session;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    // Storage denied; the in-memory copy carries this page view and nothing breaks.
  }
}

// Reports whether a session ended; inactivity and midnight UTC are its only two terminators, and a source change deliberately is not.
function isExpired(session: StorefrontSession, now: number): boolean {
  return now - session.lastSeenAt > SESSION_IDLE_MS || utcDayKey(now) !== utcDayKey(session.startedAt);
}

// Returns the live session for this tab, rotating the key when the idle or age rule ended it.
export function currentSession(now: number = Date.now()): StorefrontSession {
  const stored = readStored();
  const session = stored && !isExpired(stored, now)
    ? { ...stored, lastSeenAt: now }
    : { key: newKey(), startedAt: now, lastSeenAt: now };
  writeStored(session);
  return session;
}
